import { Box, Fade, LinearProgress, Paper } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FeatherIcon from "feather-icons-react";
import React from "react";
import Draggable from "react-draggable";
import CustomSvgIcons from "./components/svg-icons";
import { THEME_MODES } from "./helpers/constant";

export const colours = {
    primary100: "#E7A500",
    primary20: "#FAEDCC",
    primary10: "#FDF6E5",
    primaryhover: "#D09500",
    black: "#000000",
    blackShaded: "#1C2531",
    black70: "#2C2C2C",
    black80: "#737373",
    white: "#fff",
    basegray: "#F8F9FD",
    dividergray: "#DEE2F1",
    subduedgray: "#999DAC",
    critical100: "#FF6417",
    critical20: "#FADCD3",
    critical10: "#FDEEE9",
    success100: "#21A500",
    success20: "#D3EDCC",
    success10: "#E9F6E5",
    success5: "#F4FBF2",
    blue100: "#5092F4",
    blue20: "#DCE9FD",
    blue10: "#EDF4FE",
    blue5: "#F6FAFF",
    green100: "#21A500",
    green20: "#D3EDCC",
    green10: "#E9F6E5",
    green5: "#F4FBF2",
    orange100: "#E87522",
    orange20: "#FAE3D3",
    orange10: "#FDF1E9",
    orange5: "#FEF8F4",
    purple100: "#9F40C1",
    purple30: "#E7A0FF",
    purple20: "#ECD9F3",
    purple10: "#F5ECF9",
    purple5: "#FAF6FC",
    error: "#d32f2f",
    gray: "#DDDDDD",
    grayDark: "#8E8E93",
    grayDark500: "#E5E5EA",
    grayDisabled: "#CDD0DE",
    gray5: "#F8F8F8",
    gray30: "#C7C7CC",
    gray40: "#D1D1D6",
    gray50: "#525252",
    gray60: "#434343",
    gray80: "#1E1E1E",
    gray90: "#4A5468",
    yellow100: "#FFEA00",

    primary100bottomborder: "3px solid #E7A500",
    dividergraybottomborder: "1px solid #DEE2F1",
    shadowMd: {
        boxShadow: "-2px 2px 3px #D1D9E6",
        "&:hover": {
            boxShadow: "0 2px 3px #D1D9E6",
        }
    },

    vbox: {
        padding: 10,
        paddingLeft: 20,
        margin: 10,
        background: "#F8F9FD",
        boxShadow: "9px 9px 10px #D1D9E6, -9px -9px 10px #FFFFFF",
        borderRadius: "20px",
    },
    menuStyle: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textTransform: "none",
        justifyContent: "center",
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        preventDefault: true,
        textDecoration: "none",
        height: 72,
        fontSize: 12,
        flexShrink: 0,
    },
    menuStyleSelected: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textTransform: "none",
        background: "rgba(255,255,255,.20)",
        borderLeft: "4px solid #E7A500",
        borderRight: "4px solid transparent",
        justifyContent: "center",
        preventDefault: true,
        textDecoration: "none",
        height: 72,
        flexShrink: 0,
    },
};

export const CustomTooltip = ({ className, placement = "top", ...props }) => <Tooltip placement={placement} arrow={true} {...props} classes={{ popper: className }} slots={{transition: Fade}} />;

export function tabselected(selected, themeColours, FONT) {
    return {
        borderBottom: selected ? themeColours.primary100bottomborder : null,
        color: selected ? themeColours.black : themeColours.gray90,
        paddingY: 2,
        paddingX: 2,
        fontWeight: 500,
        fontSize: FONT.SIZE_14,
        textAlign: "center",
        cursor: "pointer",
    };
}

// Toggle Button Style
export function toggleButtonStyle(isSelected, themeColours, themeMode) {
    return {
        background: isSelected
            ? themeColours.primary100
            : themeMode === THEME_MODES.DARK
                ? themeColours.black
                : themeColours.white,

        boxShadow: isSelected
            ? "none"
            : "0px 2px 3px 0px rgba(0, 0, 0, 0.25) inset",

        color: isSelected
            ? (themeMode === THEME_MODES.COLOR_VISION
                ? themeColours.black
                : themeColours.white)
            : themeMode === THEME_MODES.DARK
                ? themeColours.white
                : themeColours.grayDark,

        border: themeMode === THEME_MODES.HIGH_CONTRAST && `1px solid rgba(255, 255, 255, 0.25)`,
    };
}

export function ChartTooltip(themeColours, FONT) {
    return {
        backgroundColor: themeColours.black,
        fontSize: FONT.SIZE_14,
        padding: '0.5rem 1rem',
        fontWeight: 400,
        color: themeColours.white,
        borderRadius: 4,
        border: 'none',
    };
}

export function uploadTabSelected(selected) {
    return {
        borderBottom: selected ? colours.primary100bottomborder : null,
        color: selected ? colours.black : 'grey.600',
        fontWeight: 500,
        fontSize: 14,
        cursor: "pointer",
    };
}

export function InfoTooltip({ info, iconSize = 20, themeMode }) {
    return (<CustomTooltip title={info}>
        <IconButton
            aria-label={info}
            color="primary"
            size="small"
            sx={{
                padding: .25,
                marginTop: -.25,
                marginRight: .5
            }}>
            {themeMode === THEME_MODES.COLOR_VISION ? CustomSvgIcons.infoCircleFill : <FeatherIcon icon="info" size={iconSize} />}
        </IconButton>
    </CustomTooltip>);
}

// Draggable Dialog Paper Component
export function DraggableDialog(props) {
    const nodeRef = React.useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"], .draggable-cancel'}
            bounds=".MuiDialog-root"
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

//Linear Progress With Label
export function LinearProgressWithLabel(props) {
    const { value, ...rest } = props;
    const valueNum = Number(value);
    const safeValue = isNaN(valueNum) ? 0 : valueNum;

    return (
        <Box display="flex" alignItems="center" width={180} gap={1}>
            <LinearProgress
                value={safeValue}
                aria-label={`${Math.round(safeValue)}%`}
                sx={{ flexGrow: 1 }}
                color="primary"
                {...rest}
            />
            {value != null &&
                <Typography component="span" variant="body2">
                    {`${Math.round(safeValue)}%`}
                </Typography>
            }
        </Box>
    );
}